import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/coreFields';
import {
  countUnitField,
  factoryIdField,
  factoryCountField,
} from './common/sharedFields';
import { locationFields } from './common/locationFields';
import { vendorFields } from './common/vendorFields';
import { productFields } from './common/productFields';
import { purchasedMaterialFields } from './common/productFields';
import { allCleanPowerFields } from './common/cleanPowerFields';
import { msg } from '@lingui/core/macro';
import { wasteQuantityField } from './common/wasteFields';

export const tier1FactoriesActivityDataMetadata = {
  displayName: msg({
    message: 'Supplier factories energy, waste, and water: Tier 1',
    context:
      'Activity data schema name for supplier factories energy, waste, and water: Tier 1',
  }),
  fields: {
    ...coreFields,
    ...locationFields,
    ...vendorFields,
    ...productFields,
    ...purchasedMaterialFields,
    ...allCleanPowerFields,
    biodiesel_fuel_mj: {
      displayName: msg({
        message: 'Biodiesel fuel',
        context: 'Energy from biodiesel.',
      }),
      externalDescription: msg({
        message: 'Energy from biodiesel.',
        context: 'Biodiesel fuel',
      }),
    },
    biomass_non_wood_mj: {
      displayName: msg({
        message: 'Biomass (non-wood) quantity',
        context: 'Energy from biomass (non-wood).',
      }),
      externalDescription: msg({
        message: 'Energy from biomass (non-wood).',
        context: 'Biomass (non-wood) quantity',
      }),
    },
    biomass_wood_mj: {
      displayName: msg({
        message: 'Biomass (wood) quantity',
        context: 'Energy from biomass (wood).',
      }),
      externalDescription: msg({
        message: 'Energy from biomass (wood).',
        context: 'Biomass (wood) quantity',
      }),
    },
    coal_mj: {
      displayName: msg({
        message: 'Coal quantity',
        context: 'Energy from coal.',
      }),
      externalDescription: msg({
        message: 'Energy from coal.',
        context: 'Coal quantity',
      }),
    },
    diesel_fuel_mj: {
      displayName: msg({
        message: 'Diesel fuel quantity',
        context: 'Energy from diesel fuel.',
      }),
      externalDescription: msg({
        message: 'Energy from diesel fuel.',
        context: 'Diesel fuel quantity',
      }),
    },
    district_heat_mj: {
      displayName: msg({
        message: 'District heat quantity',
        context: 'Energy from district heat.',
      }),
      externalDescription: msg({
        message: 'Energy from district heat.',
        context: 'District heat quantity',
      }),
    },
    electricity_mj: {
      displayName: msg({
        message: 'Electricity quantity',
        context: 'Energy from electricity.',
      }),
      externalDescription: msg({
        message: 'Energy from electricity.',
        context: 'Electricity quantity',
      }),
    },
    factory_id: factoryIdField,
    fuel_oil_mj: {
      displayName: msg({
        message: 'Fuel oil quantity',
        context: 'Energy from fuel oil.',
      }),
      externalDescription: msg({
        message: 'Energy from fuel oil.',
        context: 'Fuel oil quantity',
      }),
    },
    gasoline_mj: {
      displayName: msg({
        message: 'Gasoline quantity',
        context: 'Energy from gasoline.',
      }),
      externalDescription: msg({
        message: 'Energy from gasoline.',
        context: 'Gasoline quantity',
      }),
    },
    liquified_natural_gas_mj: {
      displayName: msg({
        message: 'LNG quantity',
        context: 'Energy from liquified natural gas.',
      }),
      externalDescription: msg({
        message: 'Energy from liquified natural gas.',
        context: 'LNG quantity',
      }),
    },
    liquified_petroleum_gas_mj: {
      displayName: msg({
        message: 'Liquified petroleum gas quantity',
        context: 'Energy from liquified petroleum gas.',
      }),
      externalDescription: msg({
        message: 'Energy from liquified petroleum gas.',
        context: 'LNG quantity',
      }),
    },
    natural_gas_mj: {
      displayName: msg({
        message: 'Natural gas quantity',
        context: 'Energy from natural gas.',
      }),
      externalDescription: msg({
        message: 'Energy from natural gas.',
        context: 'Natural gas quantity',
      }),
    },
    order_quantity: factoryCountField,
    // NOTE: quantity does not exist on the BAT but order_quantity is
    // renamed to quantity in the BART-EM matching process
    quantity: factoryCountField,
    order_unit: countUnitField,
    propane_mj: {
      displayName: msg({
        message: 'Propane quantity',
        context: 'Energy from propane.',
      }),
      externalDescription: msg({
        message: 'Energy from propane.',
        context: 'Propane quantity',
      }),
    },
    steam_mj: {
      displayName: msg({
        message: 'Steam quantity',
        context: 'Energy from steam.',
      }),
      externalDescription: msg({
        message: 'Energy from steam.',
        context: 'Steam quantity',
      }),
    },
    waste_kg: {
      ...wasteQuantityField,
      externalDescription: msg({
        message: 'Mass of waste.',
        context: 'Quantity of waste in kilograms',
      }),
    },
    water_liters: {
      displayName: msg({
        message: 'Water quantity',
        context: 'Volume of water.',
      }),
      externalDescription: msg({
        message: 'Volume of water.',
        context: 'Water quantity',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
